"use client"

import Image from "next/image"
import Link from "next/link"
import { ExternalLink } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog"
import { BaseModalProps, LocalizedText, StandardModalItem } from "@/types/standardModal"
import { useLocale } from "next-intl"

/**
 * Special tag types and their styling
 */
const DEFAULT_SPECIAL_TAGS = {
  "Exit": {
    background: "linear-gradient(to right, #10B981, #059669)",
    emoji: "🚀"
  },
  "Unicorn": {
    background: "linear-gradient(to right, #A855F7, #EC4899)",
    emoji: "🦄"
  },
  "Fail": {
    background: "linear-gradient(to right, #B91C1C, #7F1D1D)",
    emoji: "🔥"
  }
}

/**
 * Renders a single tag, with special styling if applicable
 */
function ModalTag({ tag, specialTags = DEFAULT_SPECIAL_TAGS }: { 
  tag: string,
  specialTags?: {[key: string]: {background: string, emoji?: string, textColor?: string}}
}) {
  // Check if this tag should have special styling (is exactly equal to a key from specialTags)
  const specialStyling = Object.keys(specialTags).find(key => tag === key)
  
  if (specialStyling) {
    const style = specialTags[specialStyling]
    const displayText = `${tag} ${style.emoji || ''}`
    
    return (
      <span key={tag} className="relative inline-flex" style={{height: '1.5rem'}}>
        <span 
          className="absolute inset-0 rounded-md" 
          style={{background: style.background}}
        ></span>
        <span 
          className="relative flex items-center px-2 py-1 text-xs rounded-md border border-transparent bg-background/80 dark:bg-background/80 text-secondary-foreground"
          style={{margin: '1px', height: 'calc(100% - 2px)'}}
        >
          {displayText}
        </span>
      </span>
    )
  }
  
  // Default tag style
  return (
    <span 
      key={tag} 
      className="inline-flex items-center px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded-md h-[1.5rem]"
    >
      {tag}
    </span>
  )
}

/**
 * Formats date/period information consistently
 */
function formatDate(date?: StandardModalItem['date']) {
  if (!date) return null
  
  const { start, end } = date
  
  if (!end || end === '0') {
    return start
  }
  
  return `${start} - ${end === 'present' ? 'Present' : end}`
}

/**
 * Gets the localized text for the given locale
 */
function getLocalizedText(text: LocalizedText | undefined, locale: string): string | undefined {
  if (!text) return undefined

  // Default to English if the requested locale isn't available
  return text[locale as keyof LocalizedText] || text.en
}

/**
 * Base Modal Component
 * 
 * A unified modal component that can handle all modal types in the application
 * with consistent styling and structure.
 */
export default function BaseModal({ item, open, onOpenChange, options = {}, locale: propLocale }: BaseModalProps) {
  // Get the current locale from next-intl hook or use the provided locale prop
  const hookLocale = useLocale()
  const locale = propLocale || hookLocale || 'en'
  
  const {
    title,
    image,
    imageDark,
    website,
    subtitle,
    description,
    date,
    note1,
    note2,
    displayTags,
    references
  } = item
  
  // Get localized content based on current locale
  const localizedSubtitle = getLocalizedText(subtitle, locale)
  const localizedDescription = getLocalizedText(description, locale)
  const localizedNote1 = getLocalizedText(note1, locale)
  const localizedNote2 = getLocalizedText(note2, locale)
  
  const { maxWidth = "525px", specialTags = DEFAULT_SPECIAL_TAGS } = options
  const formattedDate = formatDate(date)
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" style={{ maxWidth }}>
        <DialogHeader className="text-left">
          {/* Header Image Section */}
          <div className="flex justify-start mb-4">
            {website ? (
              <Link 
                href={website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                {/* For light mode or if no imageDark is provided */}
                <Image
                  src={image}
                  alt={title}
                  width={180}
                  height={48}
                  className={`h-12 w-auto max-w-[180px] object-contain object-left ${
                    imageDark ? 'dark:hidden' : 'dark:invert dark:grayscale dark:brightness-0'
                  }`}
                />
                
                {/* Dark mode image - shown only in dark mode if available */}
                {imageDark && (
                  <Image
                    src={imageDark}
                    alt={title}
                    width={180}
                    height={48}
                    className="h-12 w-auto max-w-[180px] object-contain object-left hidden dark:block dark:invert dark:grayscale dark:brightness-0"
                  />
                )}
              </Link>
            ) : (
              <>
                {/* For light mode or if no imageDark is provided */}
                <Image
                  src={image}
                  alt={title}
                  width={180}
                  height={48}
                  className={`h-12 w-auto max-w-[180px] object-contain object-left ${
                    imageDark ? 'dark:hidden' : 'dark:invert dark:grayscale dark:brightness-0'
                  }`}
                />
                
                {/* Dark mode image - shown only in dark mode if available */}
                {imageDark && (
                  <Image
                    src={imageDark}
                    alt={title}
                    width={180}
                    height={48}
                    className="h-12 w-auto max-w-[180px] object-contain object-left hidden dark:block dark:invert dark:grayscale dark:brightness-0"
                  />
                )}
              </>
            )}
          </div>
          
          {/* Title with optional website link */}
          <div className="flex items-center gap-2 mb-1">
            {website ? (
              <Link 
                href={website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                <DialogTitle className="m-0">{title}</DialogTitle>
              </Link>
            ) : (
              <DialogTitle className="m-0">{title}</DialogTitle>
            )}
            {website && (
              <Link 
                href={website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
              </Link>
            )}
          </div>

          {/* Subtitle and Date Section */}
          {(localizedSubtitle || formattedDate) && (
            <div className="flex items-center gap-2 mb-3">
              {localizedSubtitle && (
                <div className="text-sm font-medium">{localizedSubtitle}</div>
              )}
              
              {localizedSubtitle && formattedDate && (
                <div className="h-4 w-px bg-muted-foreground/30"></div>
              )}
              
              {formattedDate && (
                <div className="text-sm text-muted-foreground">
                  {formattedDate}
                </div>
              )}
            </div>
          )}

          {/* Main Content/Description */}
          <DialogDescription className="text-base">
            {localizedDescription}
          </DialogDescription>
          
          {/* Primary Note (note1) - Styled as exitInfo */}
          {localizedNote1 && (
            <p className="mt-2 mb-0 text-sm font-semibold text-primary">
              {localizedNote1}
            </p>
          )}
          
          {/* Secondary Note (note2) - Styled as observation */}
          {localizedNote2 && (
            <p className={`${localizedNote1 ? '-mt-1.5' : 'mt-2'} mb-0 text-sm italic text-muted-foreground`}>
              {localizedNote2}
            </p>
          )}
          
          {/* Tags Section */}
          {displayTags && displayTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {displayTags.map(tag => (
                <ModalTag 
                  key={tag} 
                  tag={tag} 
                  specialTags={specialTags}
                />
              ))}
            </div>
          )}

          {/* References Section */}
          {references && references.length > 0 && (
            <div className="mt-4">
              <div className="flex flex-col gap-2">
                {references.map(ref => (
                  <Link 
                    key={ref.url}
                    href={ref.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    {ref.title}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </DialogHeader>
      </DialogContent>
    </Dialog>
  )
}